import { TreeNodeData } from '@mantine/core';

import { CollectionFileWithFile, File } from 'models';

const FileHelpers = {
    isPdf: (file: Pick<File, 'mimeType'>) => file.mimeType === 'application/pdf',

    isImage: (file: Pick<File, 'mimeType'>) => file.mimeType.startsWith('image/'),

    isVideo: (file: Pick<File, 'mimeType'>) => file.mimeType.startsWith('video/'),

    createFileTree: (files: CollectionFileWithFile[], folders: string[] = []) => {
        const paths = files.map((file) => {
            return ((file.folder || '/') + '/' + file.id).replace('//', '/');
        });

        const root: TreeNodeData[] = [];

        paths.forEach((path) => {
            // Remove leading slash if present and split into parts
            const parts = path.replace(/^\//, '').split('/');
            let currentLevel = root;
            let currentPath = '';

            parts.forEach((part, index) => {
                // Build the incremental value (path) for this node
                currentPath = currentPath ? `${currentPath}/${part}` : part;

                const isLastPart = index === parts.length - 1;

                // Look for an existing node at this level
                let existingNode = currentLevel.find((node) => node.label === part);

                if (!existingNode) {
                    existingNode = {
                        label: part,
                        value: currentPath,
                    };

                    // If it's not the file (last part), initialize children array
                    if (!isLastPart) {
                        existingNode.children = [];
                    }

                    if (isLastPart) {
                        existingNode.nodeProps = files.find((file) => file.id === part);
                    }

                    currentLevel.push(existingNode);
                }

                // Move deep into the children for the next part of the path
                if (existingNode.children) {
                    currentLevel = existingNode.children;
                }
            });
        });

        folders.forEach((path) => {
            // Remove leading slash if present and split into parts
            const parts = path.replace(/^\//, '').split('/');
            let currentLevel = root;
            let currentPath = '';

            parts.forEach((part, index) => {
                // Build the incremental value (path) for this node
                currentPath = currentPath ? `${currentPath}/${part}` : part;

                // Look for an existing node at this level
                let existingNode = currentLevel.find((node) => node.label === part);

                if (!existingNode) {
                    existingNode = {
                        label: part,
                        value: currentPath,
                        children: [],
                    };

                    currentLevel.push(existingNode);
                }

                // Move deep into the children for the next part of the path
                if (existingNode.children) {
                    currentLevel = existingNode.children;
                }
            });
        });

        const abc = (a: TreeNodeData, b: TreeNodeData) => {
            if (a.children) {
                a.children.sort(abc);
            }

            if (b.children) {
                b.children.sort(abc);
            }

            if (typeof a.label === 'string' && typeof b.label === 'string') {
                if (a.children && !b.children) {
                    return -1;
                }

                if (!a.children && b.children) {
                    return 1;
                }

                return a.label.localeCompare(b.label);
            }

            return 0;
        };

        root.sort(abc);

        return root;
    },

    extension: (mimeType: string) =>
        ({
            'application/json': '.json',
            'application/pdf': '.pdf',
            'image/avif': '.avif',
            'image/gif': '.gif',
            'image/jpeg': '.jpg',
            'image/png': '.png',
            'image/svg+xml': '.svg',
            'image/webp': '.webp',
            'text/csv': '.csv',
            'text/plain': '.txt',
            'text/python': '.py',
            'text/x-python': '.py',
        })[mimeType] || '',

    stripSuffix: (filename: string) => {
        const [base = '', extension = ''] = filename.split('.');

        const parts = base.split('-');
        const stripped = parts.length > 1 ? parts.slice(0, -1).join('-') : base;

        return stripped + (extension ? `.${extension}` : '');
    },
};

export { FileHelpers };
